import type { MIMap } from '../core/MIMap';

export interface MISplitViewOptions {
  container: string | HTMLElement;
  orientation: 'horizontal' | 'vertical';
  views: MISplitViewPane[];
  synchronized?: boolean;
  syncRotation?: boolean;
  resizable?: boolean;
  dividerWidth?: number;
}

export interface MISplitViewPane {
  map?: MIMap;
  scene?: any;
  label?: string;
  layers?: any[];
  initialExtent?: any;
}

export class MISplitView {
  private container: HTMLElement;
  private orientation: 'horizontal' | 'vertical';
  private views: MISplitViewPane[];
  private synchronized: boolean;
  private syncRotation: boolean;
  private resizable: boolean;
  private dividerWidth: number;
  private paneElements: HTMLElement[] = [];
  private dividerElements: HTMLElement[] = [];
  private isDragging = false;
  private activeDivider = -1;

  constructor(options: MISplitViewOptions) {
    this.container = typeof options.container === 'string'
      ? document.getElementById(options.container) || document.createElement('div')
      : options.container;
    this.orientation = options.orientation || 'horizontal';
    this.views = options.views || [];
    this.synchronized = options.synchronized !== false;
    this.syncRotation = options.syncRotation || false;
    this.resizable = options.resizable !== false;
    this.dividerWidth = options.dividerWidth || 4;

    this.setupLayout();
    if (this.synchronized) {
      this.setupSynchronization();
    }
  }

  private setupLayout(): void {
    this.container.style.display = 'flex';
    this.container.style.flexDirection = this.orientation === 'horizontal' ? 'row' : 'column';
    this.container.style.width = '100%';
    this.container.style.height = '100%';
    this.container.style.position = 'relative';
    this.container.style.overflow = 'hidden';

    const paneCount = this.views.length;
    const paneSize = `calc(${100 / paneCount}% - ${(this.dividerWidth * (paneCount - 1)) / paneCount}px)`;

    this.views.forEach((view, index) => {
      const pane = document.createElement('div');
      pane.style.flex = `1 1 ${paneSize}`;
      pane.style.position = 'relative';
      pane.style.overflow = 'hidden';
      pane.style.minWidth = '100px';
      pane.style.minHeight = '100px';

      if (view.label) {
        const label = document.createElement('div');
        label.textContent = view.label;
        label.style.position = 'absolute';
        label.style.top = '8px';
        label.style.left = '8px';
        label.style.padding = '4px 12px';
        label.style.background = 'rgba(0,0,0,0.6)';
        label.style.color = '#fff';
        label.style.borderRadius = '4px';
        label.style.fontSize = '12px';
        label.style.fontWeight = '600';
        label.style.zIndex = '10';
        label.style.pointerEvents = 'none';
        pane.appendChild(label);
      }

      this.paneElements.push(pane);
      this.container.appendChild(pane);

      if (index < paneCount - 1) {
        const divider = document.createElement('div');
        divider.style.width = this.orientation === 'horizontal' ? `${this.dividerWidth}px` : '100%';
        divider.style.height = this.orientation === 'horizontal' ? '100%' : `${this.dividerWidth}px`;
        divider.style.background = 'var(--border, #333)';
        divider.style.cursor = this.orientation === 'horizontal' ? 'col-resize' : 'row-resize';
        divider.style.flexShrink = '0';
        divider.style.zIndex = '5';
        divider.style.transition = 'background 0.15s';

        divider.addEventListener('mouseenter', () => { divider.style.background = 'var(--primary, #666)'; });
        divider.addEventListener('mouseleave', () => { if (!this.isDragging) divider.style.background = 'var(--border, #333)'; });

        if (this.resizable) {
          divider.addEventListener('mousedown', (e) => {
            e.preventDefault();
            this.isDragging = true;
            this.activeDivider = index;
            document.addEventListener('mousemove', this.handleDrag);
            document.addEventListener('mouseup', this.handleDragEnd);
          });
        }

        this.dividerElements.push(divider);
        this.container.appendChild(divider);
      }
    });
  }

  private handleDrag = (e: MouseEvent): void => {
    if (!this.isDragging || this.activeDivider < 0) return;
    const rect = this.container.getBoundingClientRect();
    const pos = this.orientation === 'horizontal'
      ? (e.clientX - rect.left) / rect.width
      : (e.clientY - rect.top) / rect.height;
    const clampedPos = Math.max(0.1, Math.min(0.9, pos));
    const leftPane = this.paneElements[this.activeDivider];
    const rightPane = this.paneElements[this.activeDivider + 1];
    if (leftPane && rightPane) {
      leftPane.style.flex = `0 0 ${clampedPos * 100}%`;
      rightPane.style.flex = `0 0 ${(1 - clampedPos) * 100}%`;
    }
  };

  private handleDragEnd = (): void => {
    this.isDragging = false;
    this.activeDivider = -1;
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.handleDragEnd);
    this.views.forEach((view) => {
      if (view.map) {
        (view.map as any).resize?.();
      }
    });
  };

  private setupSynchronization(): void {
    this.views.forEach((sourceView, sourceIndex) => {
      if (sourceView.map) {
        const map = sourceView.map as any;
        if (map.on) {
          map.on('moveend', () => {
            if (!this.synchronized) return;
            const center = map.getCenter?.();
            const zoom = map.getZoom?.();
            this.views.forEach((targetView, targetIndex) => {
              if (targetIndex !== sourceIndex && targetView.map) {
                const targetMap = targetView.map as any;
                targetMap.setCenter?.(center);
                targetMap.setZoom?.(zoom);
              }
            });
          });
        }
      }
    });
  }

  setSynchronized(sync: boolean): void {
    this.synchronized = sync;
  }

  getViews(): (MIMap | any)[] {
    return this.views.map(v => v.map || v.scene).filter(Boolean);
  }

  setOrientation(orientation: 'horizontal' | 'vertical'): void {
    this.orientation = orientation;
    this.container.style.flexDirection = orientation === 'horizontal' ? 'row' : 'column';
    this.dividerElements.forEach(d => {
      d.style.width = orientation === 'horizontal' ? `${this.dividerWidth}px` : '100%';
      d.style.height = orientation === 'horizontal' ? '100%' : `${this.dividerWidth}px`;
      d.style.cursor = orientation === 'horizontal' ? 'col-resize' : 'row-resize';
    });
  }

  addPane(pane: MISplitViewPane): void {
    this.views.push(pane);
  }

  removePane(index: number): void {
    if (index >= 0 && index < this.views.length) {
      this.views.splice(index, 1);
    }
  }

  getPaneElement(index: number): HTMLElement | null {
    return this.paneElements[index] || null;
  }

  destroy(): void {
    this.paneElements = [];
    this.dividerElements = [];
    this.container.innerHTML = '';
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.handleDragEnd);
  }
}
